// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get variant ID from URL parameters
 */
function getVariantIdFromUrl() {
    const url = new URL(window.location.href);
    return url.searchParams.get("variant");
}

/**
 * Show error message below an input element
 */
function showErrorMessage(inputElement, message) {
    if (!inputElement) return;
    
    let errorElement = inputElement.nextElementSibling;
    if (!errorElement || !errorElement.classList.contains("error-message")) {
        errorElement = document.createElement("div");
        errorElement.className = "error-message";
        errorElement.style.color = "red";
        errorElement.style.fontSize = "14px";
        errorElement.style.marginTop = "5px";
        inputElement.parentNode.insertBefore(errorElement, inputElement.nextSibling);
    }
    errorElement.textContent = message;
}

/**
 * Clear error message below an input element
 */
function clearErrorMessage(inputElement) {
    if (!inputElement) return;
    
    const errorElement = inputElement.nextElementSibling;
    if (errorElement && errorElement.classList.contains("error-message")) {
        errorElement.remove();
    }
}

// ============================================
// FONT SIZE ADJUSTMENT
// ============================================

/**
 * Adjust font size of preview text to fit container width
 */
function adjustPreviewFontSize(previewContainer, dynamicSlide) {
    if (!previewContainer || !dynamicSlide) return;

    const maxWidth = dynamicSlide.offsetWidth;
    let fontSize = 50;
    const minFontSize = 5;

    const spans = previewContainer.querySelectorAll("span");
    if (spans.length === 0) return;

    // Set initial font size
    spans.forEach(span => {
        span.style.fontSize = `${fontSize}px`;
        span.style.whiteSpace = "nowrap";
    });

    // Decrease font size until content fits
    while (previewContainer.scrollWidth > maxWidth && fontSize > minFontSize) {
        fontSize -= 5;
        spans.forEach(span => {
            span.style.fontSize = `${fontSize}px`;
        });
    }
}

/**
 * Adjust font size based on height type selection
 */
function adjustFontSizeByHeightType(heightTypeSelect) {
    if (!heightTypeSelect) return;

    const selectedOption = heightTypeSelect.options[heightTypeSelect.selectedIndex];
    if (!selectedOption) return;

    const firstLineSpan = document.querySelector(".firstin");
    const secondLineSpan = document.querySelector(".secondli");
    const thirdLineSpan = document.querySelector(".thirdin");

    if (!firstLineSpan || !secondLineSpan || !thirdLineSpan) return;

    const firstLineFontSize = parseFloat(window.getComputedStyle(firstLineSpan).fontSize);
    const minFontSize = 5;

    let newFontSize;

    if (selectedOption.index === 0) {
        // First option: keep same size
        newFontSize = firstLineFontSize;
    } else if (selectedOption.index === 2) {
        // Third option: increase by 5px
        newFontSize = firstLineFontSize + 5;
    } else {
        // Second option: decrease by 5px (with minimum)
        newFontSize = Math.max(firstLineFontSize - 5, minFontSize);
    }

    secondLineSpan.style.fontSize = `${newFontSize}px`;
    thirdLineSpan.style.fontSize = `${newFontSize}px`;
}

// ============================================
// CHARACTER LIMIT HANDLER
// ============================================

/**
 * Initialize character limit for onlyfirstline input
 */
function initializeOnlyFirstLineLimit() {
    const inputField = document.getElementById("onlyfirstline");
    if (!inputField) return;

    const charLimit = parseInt(inputField.getAttribute("data-product-limt")) || 10;

    inputField.addEventListener("input", function() {
        const inputValue = inputField.value;

        if (inputValue.length > charLimit) {
            inputField.value = inputValue.slice(0, charLimit);
            showErrorMessage(inputField, `Limit is ${charLimit} characters only.`);
        } else {
            clearErrorMessage(inputField);
        }
    });
}

/**
 * Enable/disable font dropdown based on onlyfirstline input
 */
function initializeOnlyFirstLineFontToggle() {
    const inputField = document.getElementById("onlyfirstline");
    const fontDropdown = document.querySelector(".onlyfirstlineselectfont");

    if (!inputField || !fontDropdown) return;

    fontDropdown.disabled = true;

    inputField.addEventListener("input", function() {
        fontDropdown.disabled = inputField.value.trim() === "";
    });
}

// ============================================
// PREVIEW UPDATE HANDLER
// ============================================

class PreviewManager {
    constructor() {
        this.inputs = {
            firstline: document.getElementById("firstline"),
            secondline: document.getElementById("secondline"),
            thirdline: document.getElementById("thirdline"),
            onlyfirstline: document.getElementById("onlyfirstline")
        };

        this.dynamicSlide = document.getElementById("dynamic-slide");
        this.previewContainer = document.getElementById("preview-text");
        this.sliderContainer = document.querySelector(".product__media-list");
        this.allSlides = this.sliderContainer?.querySelectorAll(".product__media") || [];

        this.classMappings = {
            firstline: "firstin",
            secondline: "secondli",
            thirdline: "thirdin",
            onlyfirstline: "onlyfirstline"
        };
    }

    updatePreview() {
        if (!this.previewContainer) return;

        this.previewContainer.innerHTML = "";

        // Create spans for each input with value
        Object.entries(this.inputs).forEach(([id, input]) => {
            if (input && input.value.trim() !== "") {
                const lineElement = document.createElement("span");
                lineElement.textContent = input.value;
                lineElement.className = this.classMappings[id];
                this.previewContainer.appendChild(lineElement);
            }
        });

        // Adjust font size to fit container
        adjustPreviewFontSize(this.previewContainer, this.dynamicSlide);

        // Show/hide dynamic slide
        this.toggleDynamicSlide();
    }

    toggleDynamicSlide() {
        if (!this.dynamicSlide) return;

        const hasContent = Object.values(this.inputs).some(input => 
            input && input.value.trim() !== ""
        );

        if (hasContent) {
            if (!this.dynamicSlide.classList.contains("selected")) {
                this.dynamicSlide.classList.remove("hidden");
                this.dynamicSlide.classList.add("selected");

                // Remove selected from other slides
                this.allSlides.forEach(slide => {
                    if (slide !== this.dynamicSlide) {
                        slide.classList.remove("selected");
                    }
                });

                // Append to slider if not present
                if (this.sliderContainer && !this.sliderContainer.contains(this.dynamicSlide)) {
                    this.sliderContainer.appendChild(this.dynamicSlide);
                }

                // Smooth scroll to view
                setTimeout(() => {
                    this.dynamicSlide.scrollIntoView({ 
                        behavior: "smooth", 
                        block: "nearest" 
                    });
                }, 200);
            }
        } else {
            this.dynamicSlide.classList.remove("selected");
            this.dynamicSlide.classList.add("hidden");
        }
    }

    attachListeners() {
        Object.values(this.inputs).forEach(input => {
            if (input) {
                input.addEventListener("input", () => this.updatePreview());
            }
        });
    }
}

// ============================================
// VARIANT & CHARACTER LIMIT HANDLER
// ============================================

class VariantManager {
    constructor(previewManager) {
        this.previewManager = previewManager;
        this.inputs = previewManager.inputs;
        this.verifyCheckSelect = document.querySelector(".createdipeshnones .verifycheckslect");
        this.fontboxSelect = document.querySelector("#fontbox select");
        this.inputEventHandlers = {};
        this.currentUrl = window.location.href;
    }

    toggleFontboxState() {
        if (!this.fontboxSelect) return;

        const hasInput = Object.values(this.inputs).some(
            input => input && input.value.trim() !== ""
        );
        this.fontboxSelect.disabled = !hasInput;
    }

    enforceCharacterLimit(inputElement, limit) {
        if (!inputElement) return;

        if (inputElement.value.length > limit) {
            inputElement.value = inputElement.value.slice(0, limit);
            showErrorMessage(inputElement, `Character limit of ${limit} exceeded.`);
        } else {
            clearErrorMessage(inputElement);
        }

        this.previewManager.updatePreview();
    }

    updateInputsAndDropdown() {
        const variantIdFromUrl = getVariantIdFromUrl() || this.verifyCheckSelect?.value;

        if (!this.verifyCheckSelect || !variantIdFromUrl) return;

        let characterLimits = {};

        // Update selected option
        Array.from(this.verifyCheckSelect.options).forEach(option => {
            if (option.value === variantIdFromUrl) {
                option.classList.add("selected");
                this.verifyCheckSelect.value = option.value;
                
                const limit = parseInt(option.getAttribute("data-character-limit"), 10);
                characterLimits = {
                    firstline: limit,
                    secondline: limit,
                    thirdline: limit
                };
            } else {
                option.classList.remove("selected");
            }
        });

        // Apply character limits
        Object.entries(this.inputs).forEach(([id, input]) => {
            if (input && characterLimits[id]) {
                // Remove previous event listener
                if (this.inputEventHandlers[id]) {
                    input.removeEventListener("input", this.inputEventHandlers[id]);
                }

                // Create new event handler
                this.inputEventHandlers[id] = () => {
                    this.enforceCharacterLimit(input, characterLimits[id]);
                };

                // Enforce limit on current value
                this.enforceCharacterLimit(input, characterLimits[id]);

                // Add event listener
                input.addEventListener("input", this.inputEventHandlers[id]);
            }
        });

        this.previewManager.updatePreview();
        
        const heightTypeSelect = document.getElementById("height-type");
        adjustFontSizeByHeightType(heightTypeSelect);
    }

    monitorUrlChanges() {
        // Use popstate for browser navigation
        window.addEventListener("popstate", () => {
            this.updateInputsAndDropdown();
        });

        // Fallback interval check (less frequent)
        setInterval(() => {
            if (this.currentUrl !== window.location.href) {
                this.currentUrl = window.location.href;
                this.updateInputsAndDropdown();
            }
        }, 1000);
    }

    attachListeners() {
        // Fontbox state toggle
        Object.values(this.inputs).forEach(input => {
            if (input) {
                input.addEventListener("input", () => this.toggleFontboxState());
            }
        });

        // Dropdown change
        this.verifyCheckSelect?.addEventListener("change", () => {
            this.updateInputsAndDropdown();
        });
    }

    initialize() {
        this.toggleFontboxState();
        this.updateInputsAndDropdown();
        this.attachListeners();
        this.monitorUrlChanges();
    }
}

// ============================================
// HEIGHT TYPE HANDLER
// ============================================

function initializeHeightTypeHandler() {
    const heightTypeSelect = document.getElementById("height-type");
    if (!heightTypeSelect) return;

    // Set first option as selected
    if (heightTypeSelect.options.length > 0) {
        heightTypeSelect.options[0].classList.add("selected");
        adjustFontSizeByHeightType(heightTypeSelect);
    }

    // Add change event listener
    heightTypeSelect.addEventListener("change", (event) => {
        // Update selected class
        Array.from(heightTypeSelect.options).forEach(option => {
            option.classList.remove("selected");
        });
        
        const selectedOption = heightTypeSelect.options[heightTypeSelect.selectedIndex];
        selectedOption.classList.add("selected");

        // Adjust font sizes
        adjustFontSizeByHeightType(heightTypeSelect);
    });
}

// ============================================
// FONT FAMILY HANDLER
// ============================================

function initializeFontFamilyHandler() {
    const fontSelector = document.getElementById("font");
    const previewText = document.getElementById("preview-text");

    if (!fontSelector || !previewText) return;

    // Set default font
    if (fontSelector.options.length > 0) {
        fontSelector.selectedIndex = 0;
        const defaultFont = fontSelector.options[0].style.fontFamily || 
                           window.getComputedStyle(fontSelector.options[0]).fontFamily;
        previewText.style.fontFamily = defaultFont;
        fontSelector.options[0].classList.add("selected");
    }

    // Handle font change
    fontSelector.addEventListener("change", (event) => {
        const selectedOption = event.target.selectedOptions[0];

        // Update selected class
        Array.from(fontSelector.options).forEach(option => {
            option.classList.remove("selected");
        });
        selectedOption.classList.add("selected");

        // Apply font family
        const selectedFont = selectedOption.style.fontFamily || 
                            window.getComputedStyle(selectedOption).fontFamily;
        previewText.style.fontFamily = selectedFont;
    });
}

// ============================================
// INITIALIZATION
// ============================================

document.addEventListener("DOMContentLoaded", function() {
    // Initialize character limit for onlyfirstline
    initializeOnlyFirstLineLimit();

    // Initialize font dropdown toggle for onlyfirstline
    initializeOnlyFirstLineFontToggle();

    // Initialize preview manager
    const previewManager = new PreviewManager();
    previewManager.attachListeners();

    // Initialize variant manager
    const variantManager = new VariantManager(previewManager);
    variantManager.initialize();

    // Initialize height type handler
    initializeHeightTypeHandler();

    // Initialize font family handler
    initializeFontFamilyHandler();
});